import {
  button_colors_styles_default
} from "./chunk.YAGRC5Z4.js";
import {
  OverflowableComponent
} from "./chunk.B7YNIICV.js";
import {
  hasSlot
} from "./chunk.Z2CDDKXX.js";
import {
  __decorateClass,
  component_styles_default,
  theme_styles_default
} from "./chunk.U4VUMZVF.js";

// src/components/button/button.ts
import { customElement, property, query, state } from "lit/decorators.js";
import { html } from "lit/static-html.js";
import { classMap } from "lit/directives/class-map.js";
import { ifDefined } from "lit/directives/if-defined.js";

// src/components/button/button.styles.ts
import { css } from "lit";
var button_styles_default = css`
	${theme_styles_default}
	${component_styles_default}
	${button_colors_styles_default}

  	:host {
		display: inline-block;
		width: auto;
	}

	:host([size='tiny']) {
		height: var(--yotpo-button-height-tiny);
	}

	:host([size='small']) {
		height: var(--yotpo-button-height-small);
	}

	:host([size='medium']) {
		height: var(--yotpo-button-height-medium);
	}

	:host([size='large']) {
		height: var(--yotpo-button-height-large);
	}

	.button {
		display: inline-flex;
		flex-direction: row;
		justify-content: center;
		align-items: center;
		border: var(--yotpo-button-border);
		border-radius: var(--yotpo-button-border-radius);
		white-space: nowrap;
		vertical-align: middle;
		flex: none;
		align-self: stretch;
		flex-grow: 0;
		cursor: pointer;
		transition: var(--yotpo-transition-medium) border-color, var(--yotpo-transition-medium) background-color,
			var(--yotpo-transition-medium) color, var(--yotpo-transition-medium) box-shadow;
	}

	.button::-moz-focus-inner {
		border: 0;
	}

	.button:focus {
		outline: none;
	}

	.button--disabled {
		cursor: not-allowed;
	}

	/* When disabled, prevents mouse events from bubbling up */
	.button--disabled * {
		pointer-events: none;
	}

	.button:not(.button--has-prefix) .button__prefix {
		display: none;
	}

	.button:not(.button--has-suffix) .button__suffix {
		display: none;
	}

	.button__prefix,
	.button__suffix {
		display: flex;
		align-items: center;
		flex-shrink: 0;
	}

	.button__prefix ::slotted(yotpo-icon),
	.button__suffix ::slotted(yotpo-icon) {
		height: var(--yotpo-icon-size);
		font-size: var(--yotpo-icon-size);
	}

	/*
   * Size
   */

	.button--tiny {
		height: var(--yotpo-button-height-tiny);
		width: var(--yotpo-button-width-tiny);
		padding: var(--yotpo-button-vertical-spacing-tiny) var(--yotpo-button-horizontal-spacing-tiny);
	}

	.button--small {
		height: var(--yotpo-button-height-small);
		min-width: var(--yotpo-button-width-small);
		padding: var(--yotpo-button-vertical-spacing-small) var(--yotpo-button-horizontal-spacing-small);
	}

	.button--medium {
		height: var(--yotpo-button-height-medium);
		min-width: var(--yotpo-button-width-medium);
		padding: var(--yotpo-button-vertical-spacing-medium) var(--yotpo-button-horizontal-spacing-medium);
	}

	.button--large {
		height: var(--yotpo-button-height-large);
		min-width: var(--yotpo-button-width-large);
		padding: var(--yotpo-button-vertical-spacing-large) var(--yotpo-button-horizontal-spacing-large);
	}

	:host([fullwidth]) {
		width: 100%;
	}

	.button--full-width {
		width: 100%;
	}

	/*
	* Font Sizes
	*/

	.button--tiny,
	.button--small {
		font-family: var(--yotpo-font-family-text-small);
		font-weight: var(--yotpo-font-weight-text-small-regular);
		font-size: var(--yotpo-font-size-text-small);
		line-height: var(--yotpo-line-height-text-small);
	}

	.button--medium,
	.button--large {
		font-family: var(--yotpo-font-family-text-medium);
		font-size: var(--yotpo-font-size-text-medium);
		line-height: var(--yotpo-line-height-text-medium);
	}

	.button--medium {
		font-weight: var(--yotpo-font-weight-text-medium-regular);
	}

	.button--large {
		font-weight: var(--yotpo-font-weight-text-large-bold);
	}

	/*
   * Loading
   */

	.button--loading {
		position: relative;
		cursor: wait;
	}

	.button--loading .button__prefix,
	.button--loading .button__label,
	.button--loading .button__suffix {
		visibility: hidden;
	}

	.button--loading yotpo-spinner {
		position: absolute;
	}

	/*
   * Button spacing
   */

	.button--has-prefix .button__label {
		padding-left: var(--yotpo-icon-spacing);
	}

	.button--has-suffix .button__label {
		padding-right: var(--yotpo-icon-spacing);
	}

	/*
   * Overflow
   */

	.button--overflow {
		overflow: hidden;
		display: inline-block;
		text-overflow: ellipsis;
		width: 100%;
	}
`;

// src/components/button/button.ts
var YotpoButton = class extends OverflowableComponent {
  constructor() {
    super(...arguments);
    this.hasLabel = false;
    this.hasPrefix = false;
    this.hasSuffix = false;
    this.priority = "primary" /* primary */;
    this.size = "medium" /* medium */;
    this.color = "brand" /* brand */;
    this.disabled = false;
    this.loading = false;
    this.fullWidth = false;
    this.type = "button" /* button */;
    this.name = "";
  }
  connectedCallback() {
    super.connectedCallback();
    this.handleSlotChange();
  }
  click() {
    this.button.click();
  }
  handleSlotChange() {
    this.hasLabel = hasSlot(this);
    this.hasPrefix = hasSlot(this, "prefix", "yotpo-icon") || hasSlot(this, "prefix", "yotpo-svg");
    this.hasSuffix = hasSlot(this, "suffix", "yotpo-icon") || hasSlot(this, "suffix", "yotpo-svg");
  }
  handleClick(event) {
    if (this.disabled || this.loading) {
      event.preventDefault();
      event.stopPropagation();
    }
    const formId = this.getAttribute("form");
    const form = formId ? document.getElementById(formId) : this.closest("form");
    if (form && this.type === "submit" /* submit */) {
      form.requestSubmit();
    }
  }
  render() {
    const button = html`
			<button
				part="base"
				class=${classMap({
      button: true,
      "button--primary": this.priority === "primary" /* primary */,
      "button--secondary": this.priority === "secondary" /* secondary */,
      "button--tertiary": this.priority === "tertiary" /* tertiary */,
      "button--tiny": this.size === "tiny" /* tiny */,
      "button--small": this.size === "small" /* small */,
      "button--medium": this.size === "medium" /* medium */,
      "button--large": this.size === "large" /* large */,
      "button--brand": this.color === "brand" /* brand */,
      "button--destructive": this.color === "destructive" /* destructive */,
      "button--premium": this.color === "premium" /* premium */,
      "button--dark": this.color === "dark" /* dark */,
      "button--disabled": this.disabled,
      "button--loading": this.loading,
      "button--full-width": this.fullWidth,
      "button--has-label": this.hasLabel,
      "button--has-prefix": this.hasPrefix,
      "button--has-suffix": this.hasSuffix,
      "button--overflow": this.isOverflow
    })}
				?disabled=${this.disabled}
				type=${ifDefined(this.type)}
				name=${ifDefined(this.name)}
				role="button"
				aria-disabled=${this.disabled ? "true" : "false"}
				tabindex=${this.disabled ? "-1" : "0"}
				@click=${(event) => this.handleClick(event)}
			>
				<span part="prefix" class="button__prefix">
					<slot @slotchange=${this.handleSlotChange} name="prefix"></slot>
				</span>

				<span part="label" class="button__label">
					<slot @slotchange=${this.handleSlotChange}></slot>
				</span>

				<span part="suffix" class="button__suffix">
					<slot @slotchange=${this.handleSlotChange} name="suffix"></slot>
				</span>

				${this.loading ? html` <yotpo-spinner
							color=${this.priority === "primary" /* primary */ ? "var(--yotpo-color-white)" : this.color === "brand" /* brand */ ? "var(--yotpo-color-brand-70)" : this.color === "destructive" /* destructive */ ? "var(--yotpo-color-destructive-70)" : this.color === "premium" /* premium */ ? "var(--yotpo-color-premium-70)" : "var(--yotpo-color-general-100)"}
							size=${["tiny" /* tiny */, "small" /* small */].includes(this.size) ? "var(--yotpo-font-size-text-small)" : ["medium" /* medium */, "large" /* large */].includes(this.size) ? "var(--yotpo-font-size-text-medium)" : "var(--yotpo-button-font-size)"}
					  ></yotpo-spinner>` : ""}
			</button>
		`;
    return this.isOverflow ? html` <yotpo-tooltip text=${this.innerText} hoist=${true}> ${button}</yotpo-tooltip> ` : button;
  }
};
YotpoButton.styles = button_styles_default;
__decorateClass([
  query(".button")
], YotpoButton.prototype, "button", 2);
__decorateClass([
  state()
], YotpoButton.prototype, "hasLabel", 2);
__decorateClass([
  state()
], YotpoButton.prototype, "hasPrefix", 2);
__decorateClass([
  state()
], YotpoButton.prototype, "hasSuffix", 2);
__decorateClass([
  property({ reflect: true })
], YotpoButton.prototype, "priority", 2);
__decorateClass([
  property({ reflect: true })
], YotpoButton.prototype, "size", 2);
__decorateClass([
  property({ reflect: true })
], YotpoButton.prototype, "color", 2);
__decorateClass([
  property({ type: Boolean, reflect: true })
], YotpoButton.prototype, "disabled", 2);
__decorateClass([
  property({ type: Boolean, reflect: true })
], YotpoButton.prototype, "loading", 2);
__decorateClass([
  property({ type: Boolean, reflect: true })
], YotpoButton.prototype, "fullWidth", 2);
__decorateClass([
  property({ reflect: true })
], YotpoButton.prototype, "type", 2);
__decorateClass([
  property({ reflect: true })
], YotpoButton.prototype, "name", 2);
YotpoButton = __decorateClass([
  customElement("yotpo-button")
], YotpoButton);

export {
  YotpoButton
};
